<template>
  <div class="width-100">
    <collapse v-model="activeNames">
      <collapse-item title="Project Details" :key="id" name="project-details">
        <template slot="title">
          <div class="collapse-item-header" style="width: 100%">
            <div class="project-name">
            {{details.name}}
            </div>
            <div class="pull-right chat-icon">
              <img @click.stop="viewFiles" v-if="canReadFiles" src="static/img/icon-folder.png" title="Project Files" class="m-left-12" />
              <img @click.stop="openComments(id)" :id="'_' + id" :src="iconColour(getCommentCount(getParentEntityIdHash(id)))" :ref="getParentEntityIdHash(id)" title="Project Comments" class="m-left-12" />
              <div class="button-count" v-if="getCommentCount(getParentEntityIdHash(id)) > 0" >{{ getCommentCount(getParentEntityIdHash(id)) }}</div>
            </div>
          </div>
        </template>
        <div class="project-details bg-white">
          <div class="card-body" style="margin-top: -10px">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Name</label>
                  <el-input type="text"
                            name="name"
                            maxlength="256"
                            :disabled="!canEdit"
                            clearable
                            @blur="validateNameIsUnique"
                            v-validate="modelValidations.name"
                            v-model="details.name" />
                  <small class="text-danger" v-show="errors.has('name')">
                    {{ getError('name') }}
                  </small>
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group">
                  <label>Type</label>
                  <el-select class="select"
                             name="projectType"
                             data-vv-as="type"
                             size="large"
                             clearable
                             :disabled="!canEdit"
                             placeholder="Type"
                             v-validate="modelValidations.projectType"
                             v-model="details.projectType">
                    <el-option v-for="option in projectTypes"
                               class="select"
                               :value="option.value"
                               :label="option.label"
                               :key="option.label">
                    </el-option>
                  </el-select>
                  <small class="text-danger" v-show="errors.has('projectType')">
                    {{ getError('projectType') }}
                  </small>
                </div>
              </div>
              <div class="col-md-2">
                <label>
                  Stop Work
                  <el-tooltip class="item" effect="dark" content="Setting Stop Work to YES will prevent any new Jobs from being added to this project." placement="top-end">
                    <img class="icon-help" src="static/img/icon-help.png"/>
                  </el-tooltip>
                </label>
                <p-switch v-model="details.stopWork" v-if="canEdit" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
                <div v-else>{{ details.stopWork | printBool }}</div>
              </div>
              <div class="col-md-2">
                <label>
                  Active
                  <el-tooltip class="item" effect="dark" content="Setting a project to inactive will hide it from search results (unless you search for inactive projects)" placement="top-end">
                    <img class="icon-help" src="static/img/icon-help.png"/>
                  </el-tooltip>
                </label>
                <p-switch v-model="details.active" v-if="canEdit" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
                <div v-else>{{ details.active | printBool }}</div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6" style="margin-top: 15px;">
                <div class="box-visual">
                  <label>Notes</label>
                  <div class="pull-right" style="margin-top: -15px;">
                    <button type="button" v-if="this.allDepartments.length > 0 && canEdit" @click="editNote" class="btn btn-fill btn-lg btn-info el-icon-edit" style="padding: 8px;"></button>
                  </div>
                  <tabs value="Description" ref="notesTabs">
                    <tab-pane v-for="department in allDepartments" :title="`${department.name}${checkForNotesActiveDepartment(department.id)}`" :key="department.id">
                      <div v-html="getNotesByDepartmentId(department.id)" @dblclick="editNote" style="word-wrap:break-word;"></div>
                    </tab-pane>
                  </tabs>
                </div>
              </div>
              <div class="col-md-6" style="margin-top: 15px;">
                <div class="box-visual">
                  <label>
                    Metadata
                    <el-tooltip class="item" effect="dark" content="Metadata is used to store arbitrary data associated with a project" placement="top-start">
                      <img class="icon-help" src="static/img/icon-help.png"/>
                    </el-tooltip>
                  </label>
                  <button v-if="canEdit" type="button" @click="editMetadata" class="btn btn-fill btn-info btn-sm pull-right" style="margin: 0;">Edit</button>
                  <div class="add-value-container row form-group" style="clear: both;">
                    <div class="col-md-12">
                      <el-table class="table-striped"
                                row-key="id"
                                :data="localMetadata"
                                border
                                style="width: 100%">
                        <el-table-column
                          label="Name">
                          <template slot-scope="props">
                            {{ props.row.name }}
                          </template>
                        </el-table-column>
                        <el-table-column
                          label="Value">
                          <template slot-scope="props">
                            {{ props.row.value }}
                          </template>
                        </el-table-column>
                        <el-table-column
                          label="Locked">
                          <template slot-scope="props">
                            {{ props.row.locked | printBool }}
                          </template>
                        </el-table-column>
                        <el-table-column
                          label="Active">
                          <template slot-scope="props">
                            {{ props.row.active | printBool }}
                          </template>
                        </el-table-column>
                      </el-table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12" v-if="canEdit">
              <button type="button" @click="validateAndSubmit" class="btn btn-fill btn-info btn-wd pull-right" :disabled="isProcessing" style="margin-right: 20px;">Save</button>
            </div>
          </div><!-- row -->
        </div><!-- card content -->
      </collapse-item>
    </collapse>
  </div>
</template>
<script>
  import {mapFields} from 'vee-validate'
  import {Tabs, TabPane} from 'src/components/UIComponents'
  import {mapGetters} from 'vuex'
  import {Collapse, CollapseItem} from 'element-ui'
  import {
    notifySuccess,
    notifyError,
    fileSystemPath,
    objectByName,
    objectBy,
    getParentEntityIdProjectHash,
    noteIconSlice,
    objectById
  } from 'src/utilities/utils'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import CKEditor from '@ckeditor/ckeditor5-vue';
  import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
  import ckEditorConfig from 'src/utilities/ckeditor'
  import Constants from 'src/utilities/constants'
  import Permissions from 'src/utilities/permissions'
  import {OK} from 'http-status-codes'
  import {sync} from 'vuex-pathify'
  import _ from 'lodash'

  export default {
    components: {
      PSwitch,
      ckeditor: CKEditor.component,
      Collapse,
      CollapseItem,
      TabPane,
      Tabs
    },
    props: ['id'],
    computed: {
      details: sync('projects/edit@details'),
      ...mapFields([
        'name',
        'projectType'
      ]),
      ...mapGetters({
        isActiveDepartment: 'projects/edit/details/isActiveDepartment',
        projectTypes: 'types/project',
        allDepartments: 'projects/edit/departments',
        user: 'security/user'
      }),
      isProcessing() {
        return this.$store.get('processing')
      },
      canReadFiles() {
        return this.$can('read', Permissions.FILES)
      },
      canEdit() {
        return this.$can('edit', Permissions.PROJECTS)
      }
    },
    data() {
      return {
        editor: ClassicEditor,
        username: null,
        initialState: {},
        activeNames: ['project-details'],
        localMetadata: [],
        activeTab: null,
        modelValidations: {
          name: {
            required: true,
            max: 128
          },
          projectType: {
            required: true
          }
        },
        constants: Constants,
        ckEditorConfig
      }
    },
    methods: {
      async validateNameIsUnique() {
        const response = await this.$store.dispatch('projects/fetchByName', this.$store.get('projects/edit/details/name'))

        if (OK === response.status) {
          if (this.id !== response.data.id) {
            notifyError(this, 'Project names must be unique. Please enter a different project name.')
          }
        }
      },
      iconColour(commentCount) {
        return commentCount > 0 ? 'static/img/comment-red.png' : 'static/img/comment-blue.png'
      },
      getCommentCount(parentEntityIdHash) {
        return this.$store.getters['comments/count'](parentEntityIdHash)
      },
      getDepartmentByActiveTab() {
        const activeTab = objectBy(this.$refs.notesTabs.tabs, 'active', true)
        let activeTabDepartment = activeTab._props.title
        if (!activeTab) {
          console.error('Unable to load an active Tab')
          return false
        }
        if (activeTabDepartment.includes('★')) {
          activeTabDepartment = noteIconSlice(activeTabDepartment)
        }
        return objectByName(this.allDepartments, activeTabDepartment)
      },
      checkForNotesActiveDepartment(departmentId) {
        if(this.activeTab && this.$refs.notesTabs && this.$refs.notesTabs.tabs.length === this.allDepartments.length) {
          for (const tab of this.$refs.notesTabs.tabs) {
            (tab._props.title === this.activeTab || tab._props.title === this.activeTab + ' ★') ? tab.active = true : tab.active = false
          }
        }

        if (this.$store.get('projects/edit/details/notesByDepartment')[departmentId]) {
          return ' ★'
        }
        return ''
      },
      editNote() {
        const department = this.getDepartmentByActiveTab()
        if (department) {
          const notesByDepartment = this.$store.get('projects/edit/details/notesByDepartment')
          const payload = {
            department: department,
            notes: notesByDepartment[department.id] || '',
            commitTo: 'projects/edit/details/updateProjectNotes'
          }
          this.$eventHub.$emit('show-modal-3', 'Project Notes', payload, '900px')
        }
      },
      getNotesByDepartmentId(departmentId) {
        const notesByDepartment = this.$store.get('projects/edit/details/notesByDepartment')
        return notesByDepartment[departmentId] || '<p></p>'
      },
      editMetadata() {
        this.$eventHub.$emit('show-modal-1', 'Metadata', {entity: 'Project', entityId: this.id}, '900px')
      },
      viewFiles() {
        const projectName = this.$store.get('projects/edit/details/name')
        const projectId = this.$store.get('projects/edit/projectId')

        const path = fileSystemPath(projectName, projectId)
        this.$eventHub.$emit('show-modal-1', 'File Browser', path, '800px')
      },
      openComments() {
        this.$eventHub.$emit('show-modal-1', 'Comments', this.getOpenCommentsPayload())
      },
      getParentEntityIdHash(id) {
        return getParentEntityIdProjectHash(id)
      },
      getOpenCommentsPayload() {
        return {
          parentId: {
            projectId: this.id
          },
          subheading: '<strong>Project:</strong> ' + this.$store.get('projects/edit/details/name')
        }
      },
      getLabel(user) {
        if (user && user.name) {
          return user.name
        }
        return user
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      async validateAndSubmit() {
        this.errors.clear('metadata')
        if (await this.$validator.validateAll()) {
          await this.patch()
        }
      },
      async patch() {
        const response = await this.$store.dispatch('mergePatch', this.patchPayload())
        if (response.result === Constants.RETURN_CODES.SUCCESS) {
          notifySuccess(this, 'Project Updated Successfully')
        } else {
          notifyError(this, 'Failed to Update Project', response.errors)
        }
      },
      patchPayload() {
        const payload = this.$store.get('projects/edit/details/all')
        return {
          id: this.id,
          endpoint: 'projects',
          data: payload
        }
      },
      setActiveTab() {
        const activeTab = objectBy(this.$refs.notesTabs.tabs, 'active', true)
        if (activeTab) {
          this.activeTab = activeTab._props.title
        }
      },
      maybeSetDefaultDepartment() {
        const defaultDepartment = objectById(this.allDepartments, _.get(this.user, 'preferences.global.defaultDepartment'))
        if (defaultDepartment && this.$refs.notesTabs) {
          for (const tab of this.$refs.notesTabs.tabs) {
            (tab._props.title === defaultDepartment.name || tab._props.title === defaultDepartment.name + ' ★') ? tab.active = true : tab.active = false
          }
        }
      },
      watchForOpenCommentsQuerystring() {
        setTimeout(() => {
          const qs = this.$route.query
          if (qs && qs.hasOwnProperty('cp') && this.$refs.hasOwnProperty(qs.cp)) {
            this.openComments()
          }
        }, Constants.TIME.MILLIS.ONE_SECOND)
      },
      updateLocalMetadata() {
        this.localMetadata = this.$store.getters['metadata/byEntityActive']('Project')
      },
      refreshNotes() {
        this.$forceUpdate()
      }
    },
    beforeCreate() {
      this.$store.cache.dispatch('types/fetchByEntityType', 'Project')
      this.$store.cache.dispatch('departments/fetchAll')
    },
    async mounted() {
      await this.$validator.reset() // name field was loading with an error even though this.errors is empty, this fixes it
      this.$eventHub.$on('projectDetailsLoaded', this.watchForOpenCommentsQuerystring)
      this.$eventHub.$on('projectMetadataUpdated', this.updateLocalMetadata)
      this.$eventHub.$on('projectNotesUpdated', this.refreshNotes)

      // table not loading data from vuex properly, use local reference
      await this.$store.dispatch('metadata/fetchByEntityAndEntityId', {entity: 'Project', entityId: this.id})
      this.updateLocalMetadata()
      this.maybeSetDefaultDepartment()
    },
    destroyed() {
      this.$eventHub.$off('projectNotesUpdated')
      this.$eventHub.$off('projectDetailsLoaded')
      this.$eventHub.$off('projectMetadataUpdated')
    },
    watch: {
      '$route.query'(qs) {
        this.watchForOpenCommentsQuerystring()
      },
      isActiveDepartment: {
        handler: function (isActiveDepartment) {
          if (isActiveDepartment) {
            this.setActiveTab()
            return
          }
          this.activeTab = null
        }
      }
    }
  }
</script>
<style>
  .icon-help {
    padding-bottom: 3px;
  }
  .box-visual {
    width: 100%;
    background-color: #f5f7fa;
    padding: 20px;
  }
</style>
<style scoped>
  .project-name {
    display: inline-block;
    width: 85%;
  }
</style>
